import { FormControl, Input, InputLabel, OutlinedInput, TextFieldProps } from '@mui/material';
import ClearInputAdornment from './ClearInputAdornment';
import FormLabel from './FormLabel';
import classNames from 'classnames/bind';
import { HTMLAttributes, forwardRef, useEffect, useMemo, useRef, useState } from 'react';
import { AppInputCustomProps, CustomFieldProps } from './types';
import { IMask, IMaskInput } from 'react-imask';
import DisabledTooltip from '@components/DisabledTooltip';
import useDebouncer from '@hooks/useDebounce';

import styles from './Form.module.css';
const classNamesCtx = classNames.bind(styles);

type AppDateTimeRangeFieldProps = TextFieldProps &
	AppInputCustomProps & {
		/** Override the expected onchange arguments to manually invoke with accepted value */
		onValueChange: (value: string | null) => void;
	};

type DateTimeRangeBaseProps = HTMLAttributes<HTMLInputElement> & {
	/** Required input name. Based HTML Input Element is optional. */
	name: string;
	/** Input value */
	value: string;
	/** Override the expected onchange arguments to manually invoke with accepted value */
	onValueChange: (value: string | null) => void;
	/** Flag indicating the input is in a disabled state */
	disabled?: boolean;

	maxValue?: number;
};

/** React iMask Input Component applying the a Date with Time Range MM/dd/YY HH:mm - HH:mm */
const DateTimeRangeMaskCustom = forwardRef<HTMLInputElement, DateTimeRangeBaseProps>(function RenderTextMaskCustom(props, ref) {
	return (
		<IMaskInput
			id={props.name}
			name={props.name}
			type='text'
			className={props.className}
			value={props.value}
			disabled={props.disabled}
			mask={'m/d/00 t0:00 - t0:00'}
			definitions={{
				t: /[0-2]/
			}}
			blocks={{
				m: {
					mask: IMask.MaskedRange,
					from: 1,
					to: 12,
					maxLength: 2
				},
				d: {
					mask: IMask.MaskedRange,
					from: 1,
					to: 31,
					maxLength: 2
				},
				y: {
					mask: IMask.MaskedRange,
					from: 10,
					to: 99,
					maxLength: 2
				}
			}}
			placeholder='01/01/00 00:00 - 00:00'
			inputRef={ref}
			onAccept={(value: string) => {
				props.onValueChange(value ? value : null);
			}}
			onBlur={props.onBlur}
			overwrite
		/>
	);
});

/**
 * Wrapper around MUI TextField to provide application-wide default behaviors.
 */
export function DateTimeRangeField({ customProps, ...props }: AppDateTimeRangeFieldProps) {
	const inputRef = useRef<HTMLInputElement | null>(null);

	const textFieldClass = classNamesCtx({
		input: true,
		dirty: customProps.isDirty,
		error: props.error,
		disabled: props.disabled,
		[props.className ?? '']: Boolean(props.className)
	});

	const InputComponent = props.variant === 'standard' ? Input : OutlinedInput;

	useEffect(() => {
		if (props.focused) {
			inputRef.current?.focus();
		}
	}, [props.focused]);

	return (
		<DisabledTooltip message={customProps.disabledReason}>
			<FormControl
				variant='outlined'
				className={textFieldClass}
			>
				<InputLabel
					htmlFor={props.name}
					className={props.variant ? styles['date-range-input-label-standard-variant'] : undefined}
				>
					<FormLabel
						text={customProps.labelText}
						error={props.error && !props.disabled}
						errorText={customProps.errorText}
						required={customProps.required}
					/>
				</InputLabel>
				<InputComponent
					id={props.name}
					className={styles['input-component']}
					inputComponent={DateTimeRangeMaskCustom as never}
					inputProps={{
						onBlur: props.onBlur,
						value: props.value,
						onValueChange: props.onValueChange,
						name: props.name,
						disabled: props.disabled,
						ref: inputRef
					}}
					label={`${customProps.labelText} ${props.error ? 'er' : ''}`} // This is used for the break in the fieldset, since we add an error icon when it is an error add extra space
					error={props.error}
					endAdornment={
						<ClearInputAdornment
							onClear={customProps.onClear}
							inputValue={props.value}
							inputIsDisabled={props.disabled}
						/>
					}
				/>
			</FormControl>
		</DisabledTooltip>
	);
}

function asDateTimeRangeInput() {
	return ({ field, form, ...props }: CustomFieldProps) => {
		const { value, initialValue, error } = form.getFieldMeta<string | null>(field.name);

		const { dirty, validateForm } = form;

		const isDirty = useMemo(() => {
			if (dirty) {
				return value !== initialValue;
			}
			return false;
		}, [value, initialValue, dirty]);

		/** Keeping track of the input state locally for performance reasons when typing into the input */
		const [myValue, setMyValue] = useState<string | null>(value);

		/** If the formik value changes outside of this component, update our local state to reflect */
		useEffect(() => {
			setMyValue((state) => {
				if (value !== state) {
					return value ?? '';
				}
				return state;
			});
			void validateForm();
		}, [value, validateForm]);

		/** When the local state has changed, compare to the form value. After 1/2 a second update the form if the value differs. */
		useDebouncer(
			() => {
				if (myValue !== value) {
					void form.setFieldTouched(field.name, true);
					void form.setFieldValue(field.name, myValue, true);
					void validateForm();
				}
			},
			[myValue],
			300
		);

		return (
			<DateTimeRangeField
				name={field.name}
				placeholder={props.placeholder}
				className={props.className}
				slotProps={{
					input: {
						startAdornment: props.startAdornmentIcon,
						endAdornment: props.endAdornmentIcon
					}
				}}
				value={myValue}
				onValueChange={(value: string | null) => {
					setMyValue(value);
				}}
				disabled={props.disabled}
				error={Boolean(error)}
				customProps={{
					labelText: props.label ?? '',
					isDirty: isDirty,
					errorText: error,
					onClear: () => {
						setMyValue(null);
					},
					disabledReason: props.disabledReason,
					required: props.required
				}}
			/>
		);
	};
}

export const FormikDateTimeRangeInput = asDateTimeRangeInput();
